import React from "react";

import { Badge, Card, Progress, Skeleton } from "@amphore-dev/amphore-lib";

import { getDeltaColorToken } from "@/utils/UAnalytics";
import { cn } from "@/utils/cn";

export interface IStatTileProps {
	label: React.ReactNode;
	value: React.ReactNode;
	/** A line under the value: a comparison base, a unit, a count. */
	sub?: React.ReactNode;
	/** Change against the previous period, in percent; `null` = no base to compare to. */
	delta?: number | null;
	/** 0-100, drawn as a bar under the value. */
	progress?: number;
	isLoading?: boolean;
	className?: string;
}

const formatDelta = (delta: number) =>
	`${delta > 0 ? "+" : ""}${Math.round(delta)} %`;

/** One figure of a detail page: label, big value, and a delta badge or a progress bar. */
export const StatTile: React.FC<IStatTileProps> = ({
	label,
	value,
	sub,
	delta,
	progress,
	isLoading = false,
	className,
}) => {
	return (
		<Card
			className={cn(["flex flex-col gap-1.5", className])}
			data-testid="stat-tile"
		>
			<span className="text-xs text-neutral-500">{label}</span>
			{isLoading ? (
				<Skeleton className="h-7 w-24" />
			) : (
				<span className="text-2xl leading-none font-medium">
					{value}
				</span>
			)}
			{progress !== undefined && !isLoading && (
				<Progress
					value={Math.min(100, Math.max(0, progress))}
					color="primary"
					size="sm"
					className="mt-1"
				/>
			)}
			{(sub || delta != null) && !isLoading && (
				<span className="flex items-center gap-2 text-xs text-neutral-500">
					{delta != null && (
						<Badge
							size="sm"
							variant="tint"
							color={getDeltaColorToken(delta)}
							pill
						>
							{formatDelta(delta)}
						</Badge>
					)}
					{sub}
				</span>
			)}
		</Card>
	);
};
